import { VOOrchestraLayoutPlace, VOOrchestraTrack } from "../model/DataStructure.js";
import { Polygon } from "./Polygon.js";
import { Sound } from "../audio/Sound.js";
import { Soundwave, SoundwaveChangeEvent } from "./Soundwave.js";
import { NavigationButton } from "./Navigation.js";

export interface OrchestraInstrumentOptions {
    data: VOOrchestraTrack;
    backgroundGradient?: boolean;
    shadow?: boolean;
    shadowAngle?: number;
    shadowDistance?: number;
    audioContext: AudioContext;
    destination: AudioNode;
    sound: Sound;
    totalDuration: number;
    volume?: number;
    container: HTMLElement;
}

export class OrchestraInstrument {

    public readonly onSeek = e5.Signal.create<[SoundwaveChangeEvent]>();

    public readonly sound: Sound;
    public readonly data: VOOrchestraTrack;
    public readonly element: HTMLDivElement;

    private _options: OrchestraInstrumentOptions;
    private _source: AudioBufferSourceNode;
    private _gain: GainNode;
    private _volume: number = -1;
    private _soundwave: Soundwave;
    private _polygon: Polygon;

    constructor(options: OrchestraInstrumentOptions) {
        this._options = e5.object.merge({
            volume: 0.5,
            shadow: false,
            backgroundGradient: false
        } as OrchestraInstrumentOptions, options);

        this.init();
    }

    private async init() {

        const options = this._options;

        (this as Mutable<OrchestraInstrument>).element = document.createElement("div");
        (this as Mutable<OrchestraInstrument>).sound = options.sound;
        (this as Mutable<OrchestraInstrument>).data = options.data;

        this.element.className = "orchestra-instrument orchestra-instrument--hidden";
        this.element.style.left = options.data.place.x + "px";
        this.element.style.top = options.data.place.y + "px";
        this.element.setAttribute("data-orchestrainstrument", options.data.instrument.id + "");
        this.element.innerHTML = `
        <div class="orchestra-instrument__image-wrapper">
            <img class="orchestra-instrument__image" 
                width="${options.data.instrument.image.width}"
                height="${options.data.instrument.image.height}"
                src="${e5.Text.escapeHtml(options.data.instrument.image.url)}"
                draggable="false"
            ></img>
        </div>
        <div class="orchestra-instrument__name">${e5.Text.escapeHtml(options.data.instrument.name)}</div>
        <div class="orchestra-instrument__soundwave"></div>
        `;

        this._polygon = new Polygon({
            shape: options.data.place.shape,
            color: `var(${options.data.instrument.color_name})`,
            gradient: options.backgroundGradient,
            shadow: options.shadow,
            shadowAngle: options.shadowAngle,
            shadowDistance: options.shadowDistance,
            container: this.element,
            prepend: true,
            background: true
        });

        this.element.querySelector(".orchestra-instrument__soundwave")
            .classList.add("orchestra-instrument__soundwave--loaded");

        this._soundwave = new Soundwave({
            infos: [{
                sound: this.sound,
                volume: 1
            }],
            colorBaseLineLeft: "rgba(0, 0, 0, 0.2)",
            colorBaseLineRight: "rgba(0, 0, 0, 0.2)",
            totalDuration: this._options.totalDuration,
            container: this.element.querySelector(".orchestra-instrument__soundwave")
        });
        this._soundwave.element.querySelector<HTMLElement>(".soundwave__wrapper--left").style.opacity = "0.75";

        this._soundwave.onSeek.add((e) => {
            if (this.visible) {
                this.onSeek.dispatch(e);
            }
        });

        this._gain = this._options.audioContext.createGain();
        this._gain.connect(this._options.destination);
        this.setVolume(options.volume);

        if (options.container) {
            options.container.appendChild(this.element);
        }

        return true;
    }

    public getVolume(): number {
        return this._volume;
    }

    public setVolume(value: number, time: number = 0) {
        value = e5.Calc.clamp(value, 0, 1);
        if (this._volume === value) {
            return;
        }
        this._volume = value;

        gsap.to(this._gain.gain,
            {
                duration: time, value: value, onUpdate: () => {
                    const opacityRange = 80;
                    const opacity = ((100 - opacityRange) + this._gain.gain.value * opacityRange).toFixed(2) + "%";

                    this._polygon.foreground.style.opacity = opacity;
                    this._soundwave.element.querySelector<HTMLElement>(".soundwave__wrapper--right").style.opacity = opacity;
                }
            });
    }

    public play(start: number, offset: number): void {

        if (this._source) {
            this._source.stop(0);
            this._source.disconnect();
            this._source = null;
        }

        this._source = this._options.audioContext.createBufferSource();
        this._source.channelInterpretation = "speakers";
        this._source.buffer = this.sound.buffer;
        this._source.connect(this._gain);
        this._source.start(start, offset);
    }

    public updateTime(time: number): void {
        if (!this.sound
            || !this._source) {
            return;
        }

        this._soundwave.time = time;
    }

    public get visible(): boolean {
        return !this.element.classList.contains("orchestra-instrument--hidden");
    }

    public set visible(value: boolean) {
        this.element.classList.toggle("orchestra-instrument--hidden", !value);
    }
}